import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/auth-context';
import { useEmailChange } from '@/hooks/use-profile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { VerificationCodeInput } from '@/components/verification-code-input';
import { CountdownTimer } from '@/components/countdown-timer';
import {
  emailChangeVerifySchema,
  type EmailChangeVerifyFormData
} from '@/schemas/profile.schema';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { suggestEmail } from '@/lib/email-typo';
import { validateEmail } from '@/lib/email-validator';

type Step = 'request' | 'verify' | 'success';

export default function ChangeEmailPage() {
  const { t } = useTranslation(['profile', 'validation']);
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Admin/staff return to their settings Profile tab; customers to home.
  const accountHref =
    user?.role === 'admin'
      ? '/admin/settings?tab=myProfile'
      : user?.role === 'staff'
        ? '/staff/settings?tab=myProfile'
        : '/';

  const [step, setStep] = useState<Step>('request');
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [expiresAt, setExpiresAt] = useState<Date>(new Date(Date.now() + 30 * 60 * 1000));
  const { requestEmailChange, verifyEmailChange, isRequesting, isVerifying } = useEmailChange();

  // Build the request-step schema inline so validation messages and the
  // EmailGuard rules (format / typo / disposable) run client-side with the
  // active locale's strings.
  const requestSchema = z.object({
    newEmail: z.string().min(1, t('validation:email.required')).superRefine((val, ctx) => {
      const result = validateEmail(val);
      if (result.ok) return;
      const msg =
        result.code === 'format'
          ? t('validation:email.invalid_format')
          : result.code === 'typo'
            ? (result.suggestion
                ? t('validation:email.suspicious_typo', { suggestion: result.suggestion })
                : t('validation:email.suspicious_typo_generic'))
            : t('validation:email.disposable');
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg });
    })
  });

  type RequestFormData = z.infer<typeof requestSchema>;

  const requestForm = useForm<RequestFormData>({
    resolver: zodResolver(requestSchema),
    mode: 'onChange',
    defaultValues: { newEmail: '' }
  });

  const verifyForm = useForm<EmailChangeVerifyFormData>({
    resolver: zodResolver(emailChangeVerifySchema)
  });

  const handleRequestEmailChange = async (data: RequestFormData) => {
    try {
      await requestEmailChange(data as { newEmail: string });
      setNewEmail(data.newEmail!);
      setExpiresAt(new Date(Date.now() + 30 * 60 * 1000));
      setStep('verify');
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleVerifyEmailChange = async () => {
    if (verificationCode.length !== 6) return;

    try {
      await verifyEmailChange({ verificationCode });
      setStep('success');
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleResendCode = async () => {
    try {
      await requestEmailChange({ newEmail });
      setExpiresAt(new Date(Date.now() + 30 * 60 * 1000));
      setVerificationCode('');
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  if (!user) {
    return null;
  }

  const watchedNewEmail = requestForm.watch('newEmail');
  const suggestion = watchedNewEmail ? suggestEmail(watchedNewEmail) : null;
  // Disable submit until react-hook-form considers the form valid (validates
  // format + typo + disposable via the schema above).
  const isRequestSubmitDisabled =
    isRequesting || !requestForm.formState.isValid || !watchedNewEmail;

  return (
    <div className="container max-w-2xl mx-auto py-8 px-4">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => setLocation(accountHref)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('profile:changeEmailPage.back')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {t('profile:changeEmailPage.title')}
          </CardTitle>
          <CardDescription>
            {step === 'request' && t('profile:changeEmailPage.description.request')}
            {step === 'verify' && t('profile:changeEmailPage.description.verify')}
            {step === 'success' && t('profile:changeEmailPage.description.success')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: Request Email Change */}
          {step === 'request' && (
            <form onSubmit={requestForm.handleSubmit(handleRequestEmailChange)} className="space-y-4">
              <Alert>
                <AlertDescription>
                  <p className="font-medium">{t('profile:changeEmailPage.request.currentEmail', { email: user.email })}</p>
                  <p className="text-sm text-soft mt-1">
                    {t('profile:changeEmailPage.request.info')}
                  </p>
                </AlertDescription>
              </Alert>

              <FormField
                label={t('profile:changeEmailPage.request.newEmailLabel')}
                error={requestForm.formState.errors.newEmail?.message}
              >
                <Input
                  id="newEmail"
                  type="email"
                  placeholder={t('profile:changeEmailPage.request.newEmailPlaceholder')}
                  {...requestForm.register('newEmail')}
                />
                {suggestion && suggestion !== watchedNewEmail.trim() && (
                  <button
                    type="button"
                    className="text-sm text-primary underline mt-1"
                    onClick={() => requestForm.setValue('newEmail', suggestion, { shouldValidate: true })}
                  >
                    {t('profile:changeEmailPage.request.didYouMean', { suggestion })}
                  </button>
                )}
              </FormField>

              <Button type="submit" className="w-full" disabled={isRequestSubmitDisabled}>
                {isRequesting
                  ? t('profile:changeEmailPage.request.submitting')
                  : t('profile:changeEmailPage.request.submit')}
              </Button>
            </form>
          )}

          {/* Step 2: Verify Code */}
          {step === 'verify' && (
            <div className="space-y-6">
              <Alert>
                <AlertDescription>
                  <p className="font-medium">{t('profile:changeEmailPage.verify.codeSentTo')}</p>
                  <p className="text-sm mt-1">{newEmail}</p>
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-center block">{t('profile:changeEmailPage.verify.codeLabel')}</Label>
                  <VerificationCodeInput
                    value={verificationCode}
                    onChange={setVerificationCode}
                    disabled={isVerifying}
                    autoFocus
                  />
                  {verifyForm.formState.errors.verificationCode && (
                    <p className="text-sm text-destructive text-center">
                      {verifyForm.formState.errors.verificationCode.message}
                    </p>
                  )}
                </div>

                <div className="text-center">
                  <CountdownTimer expiresAt={expiresAt} />
                </div>

                <Button
                  onClick={handleVerifyEmailChange}
                  className="w-full"
                  disabled={verificationCode.length !== 6 || isVerifying}
                >
                  {isVerifying
                    ? t('profile:changeEmailPage.verify.submitting')
                    : t('profile:changeEmailPage.verify.submit')}
                </Button>

                <Button
                  variant="outline"
                  onClick={handleResendCode}
                  className="w-full"
                  disabled={isRequesting}
                >
                  {t('profile:changeEmailPage.verify.resend')}
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => setStep('request')}
                  className="w-full"
                >
                  {t('profile:changeEmailPage.verify.useDifferent')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <div className="text-center space-y-6 py-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-h1 text-ink">{t('profile:changeEmailPage.success.title')}</h3>
                <p className="text-soft">
                  {t('profile:changeEmailPage.success.message')}
                </p>
                <p className="text-lg font-medium">{newEmail}</p>
              </div>

              <Button
                onClick={() => setLocation(accountHref)}
                className="w-full"
              >
                {t('profile:changeEmailPage.success.back')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
